import { percentOfHundred, undefDash } from 'utils/formatters/GolfNumberFormatter';
import { toQueryString } from 'utils/UrlUtil';

export const ROUND_TYPES = {
    ALL_18_HOLES: 'ALL_18_HOLES',
    FRONT_9_HOLES: 'FRONT_9_HOLES',
    BACK_9_HOLES: 'BACK_9_HOLES'
};

export const ROUND_TYPE_KEYS = {
    ALL_18_HOLES: 'leaderboard.round.type.full',
    FRONT_9_HOLES: 'leaderboard.round.type.front.9',
    BACK_9_HOLES: 'leaderboard.round.type.back.9'
};

export const getRoundTypeStats = (golfCourseStats, roundType) => {
    if (golfCourseStats && golfCourseStats.roundTypeStats && golfCourseStats.roundTypeStats.length) {
        const roundStats = golfCourseStats.roundTypeStats.find((roundTypeStat) => {
            return roundTypeStat.statsRoundType === roundType;
        });
        if (roundStats) {
            return roundStats;
        }
        return undefined;
    }
    return undefined;
};

export const getScoreChartData = (roundTypeStats) => {
    const scorecards = [];
    const scores = [];

    const scoresPerScorecard = roundTypeStats.scoresPerScorecard;

    scoresPerScorecard.forEach((scorecard) => {
        if (scorecard.score) {
            scorecards.push(scorecard);
            scores.push(scorecard.score);
        }
    });

    const min = roundTypeStats.lifetimeBestAndWorst.bestRoundStrokes;
    const max = roundTypeStats.lifetimeBestAndWorst.worstRoundStrokes;
    const avg = roundTypeStats.avgScore;

    const chartData = [];

    for (let i = min; i <= max; i++) {
        let matchedScorecards = scorecards.filter((scorecard) => {
            return scorecard.score === i;
        });

        const numScorecards = Math.min(matchedScorecards.length, 4);

        chartData.push({
            low: numScorecards === 0 ? 0 : -numScorecards,
            high: matchedScorecards.length
        });
    }

    return {
        chartData: chartData,
        max: max,
        min: min,
        avg: avg,
        formatter: (value) => {
            return undefDash(value, 0);
        },
        isAvgIndex: (index) => {
            return index === Math.floor(avg - min);
        }
    };
};

export const getGirChartData = (roundTypeStats) => {
    const scorecards = [];
    const scores = [];

    const scoresPerScorecard = roundTypeStats.scoresPerScorecard;

    scoresPerScorecard.forEach((scorecard) => {
        if (scorecard.avgGir) {
            scorecards.push(scorecard);
            scores.push(scorecard.avgGir * 100);
        }
    });

    const min = roundTypeStats.lifetimeBestAndWorst.worstRoundGir * 100;
    const max = roundTypeStats.lifetimeBestAndWorst.bestRoundGir * 100;

    const avg = roundTypeStats.avgGir * 100;

    const chartData = [];

    for (let i = min; i <= max; i++) {
        let matchedScorecards = scorecards.filter((scorecard) => {
            return scorecard.avgGir * 100 === i;
        });

        const numScorecards = Math.min(matchedScorecards.length, 4);

        chartData.push({
            low: numScorecards === 0 ? 0 : -numScorecards,
            high: matchedScorecards.length
        });
    }

    return {
        chartData: chartData,
        max: max,
        min: min,
        avg: avg,
        formatter: percentOfHundred,
        isAvgIndex: (index) => {
            return index === Math.floor(avg - min);
        }
    };
};

export const getPphChartData = (roundTypeStats) => {
    const scorecards = [];
    const scores = [];

    const scoresPerScorecard = roundTypeStats.scoresPerScorecard;

    scoresPerScorecard.forEach((scorecard) => {
        if (scorecard.avgPuttsPerHole) {
            scorecards.push(scorecard);
            scores.push(scorecard.avgPuttsPerHole);
        }
    });

    const min = roundTypeStats.lifetimeBestAndWorst.bestRoundPuttsPerHole;
    const max = roundTypeStats.lifetimeBestAndWorst.worstRoundPuttsPerHole;
    const avg = roundTypeStats.avgPuttsPerHole;

    const chartData = [];

    for (let i = min; i <= max; i += 0.1) {
        let matchedScorecards = scorecards.filter((scorecard) => {
            return Math.round(10 * scorecard.avgPuttsPerHole) / 10 === Math.round(10 * i) / 10; // equal to the tenths place
        });

        const numScorecards = Math.min(matchedScorecards.length, 4);

        chartData.push({
            low: numScorecards === 0 ? 0 : -numScorecards,
            high: matchedScorecards.length
        });
    }

    return {
        chartData: chartData,
        max: max,
        min: min,
        avg: avg,
        formatter: (value) => {
            return undefDash(value, 1);
        },
        isAvgIndex: (index) => {
            return index / 10 === Math.round(10 * (avg - min)) / 10;
        }
    };
};

export const coursePageUrl = (courseSnapshotId, courseGlobalId, backNineCourseGlobalId) => {
    const queryString = toQueryString({
        courseSnapshotId: courseSnapshotId,
        courseGlobalId: courseGlobalId,
        backNineCourseGlobalId: backNineCourseGlobalId
    });
    return `/golf-course-stats${queryString}`;
};

export const courseScorecardPageUrl = (courseSnapshotId, courseGlobalId, backNineCourseGlobalId) => {
    const queryString = toQueryString({
        courseSnapshotId: courseSnapshotId,
        courseGlobalId: courseGlobalId,
        backNineCourseGlobalId: backNineCourseGlobalId
    });
    return `/golf-course-stats/scorecards${queryString}`;
};

/**
 * The id used to store and reference golf course data in the Redux store
 */
export const getGolfCourseStoreId = (courseSnapshotId, courseGlobalId, fromDate) => {
    return fromDate && courseGlobalId ? `${courseGlobalId}-${fromDate}` : courseSnapshotId;
};
